
import { useState, useEffect, useCallback } from 'react';
import { Page } from '@/types/page';

interface UsePageRotationProps {
  leftPages: Page[];
  rightPages: Page[];
  fullscreenPages: Page[];
  autoScroll?: boolean;
  rotationInterval: number;
  onPageChange?: () => void;
}

interface UsePageRotationResult {
  currentLeftPage: Page | null;
  currentRightPage: Page | null;
  currentFullscreenPage: Page | null;
  nextLeftPage: Page | null;
  nextRightPage: Page | null;
  nextFullscreenPage: Page | null;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  isTransitioning: boolean;
}

export const usePageRotation = ({
  leftPages,
  rightPages,
  fullscreenPages,
  autoScroll = true,
  rotationInterval,
  onPageChange
}: UsePageRotationProps): UsePageRotationResult => {
  // Create a list of all page "scenes" - each scene is either a fullscreen page or a left+right combination
  const [allScenes, setAllScenes] = useState<Array<{
    type: 'fullscreen' | 'regular';
    fullscreenPage: Page | null;
    leftPage: Page | null;
    rightPage: Page | null;
  }>>([]);
  
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Build the scenes array whenever the page lists change
  useEffect(() => {
    const scenes = [];
    
    // Add all fullscreen pages as individual scenes
    if (fullscreenPages.length > 0) {
      for (const page of fullscreenPages) {
        scenes.push({
          type: 'fullscreen',
          fullscreenPage: page,
          leftPage: null,
          rightPage: null
        });
      }
    }
    
    // If there are left or right pages, create regular scenes
    if (leftPages.length > 0 || rightPages.length > 0) {
      // Get the maximum number of pages between left and right
      const maxRegularPages = Math.max(leftPages.length, rightPages.length);
      
      for (let i = 0; i < maxRegularPages; i++) {
        scenes.push({
          type: 'regular',
          fullscreenPage: null,
          leftPage: i < leftPages.length ? leftPages[i] : null,
          rightPage: i < rightPages.length ? rightPages[i] : null
        });
      }
    }
    
    console.log("Created rotation scenes:", scenes);
    setAllScenes(scenes);
    
    // Reset current index if it's out of bounds
    if (scenes.length > 0 && currentSceneIndex >= scenes.length) {
      setCurrentSceneIndex(0);
    }
  }, [leftPages, rightPages, fullscreenPages]);

  const handlePageTransition = useCallback(() => {
    setIsTransitioning(true);
    if (onPageChange) {
      onPageChange();
    }
    setTimeout(() => setIsTransitioning(false), 500);
  }, [onPageChange]);

  const goToNextPage = useCallback(() => {
    if (allScenes.length === 0) return;
    
    handlePageTransition();
    setCurrentSceneIndex(prev => (prev + 1) % allScenes.length);
  }, [allScenes.length, handlePageTransition]);

  const goToPrevPage = useCallback(() => {
    if (allScenes.length === 0) return;
    
    handlePageTransition();
    setCurrentSceneIndex(prev => 
      prev === 0 ? allScenes.length - 1 : prev - 1
    );
  }, [allScenes.length, handlePageTransition]);

  // Auto rotation effect
  useEffect(() => {
    if (!autoScroll || allScenes.length === 0) return;

    // Get rotation time for the current page
    const currentScene = allScenes[currentSceneIndex];
    const currentLeftPage = currentScene?.leftPage;
    const useCustomTime = currentLeftPage?.useCustomRotationTime || false;
    const currentRotationTime = useCustomTime && currentLeftPage?.rotationTimeMs ? 
      currentLeftPage.rotationTimeMs : 
      rotationInterval;
    
    console.log(`Setting rotation timer for ${currentRotationTime} seconds (${useCustomTime ? 'custom' : 'global'} setting)`);
    
    const interval = setInterval(() => {
      goToNextPage();
    }, currentRotationTime * 1000);

    return () => clearInterval(interval);
  }, [goToNextPage, rotationInterval, autoScroll, allScenes.length, currentSceneIndex]);

  // Get the current scene
  const currentScene = allScenes[currentSceneIndex] || {
    type: 'regular', 
    fullscreenPage: null, 
    leftPage: null, 
    rightPage: null
  };

  // Get the next scene (for preloading)
  const nextSceneIndex = (currentSceneIndex + 1) % Math.max(1, allScenes.length);
  const nextScene = allScenes[nextSceneIndex] || {
    type: 'regular',
    fullscreenPage: null,
    leftPage: null,
    rightPage: null
  };

  return {
    currentLeftPage: currentScene.leftPage,
    currentRightPage: currentScene.rightPage,
    currentFullscreenPage: currentScene.fullscreenPage,
    nextLeftPage: nextScene.leftPage,
    nextRightPage: nextScene.rightPage,
    nextFullscreenPage: nextScene.fullscreenPage,
    goToPrevPage,
    goToNextPage,
    isTransitioning
  };
};
